import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { Drawer } from 'syngenta-digital-cropwise-react-ui-kit';
import SearchInputLandingProperties from 'screens/Property/PropertiesLanding/components/SearchInputLandingProperties';
import PropertyMapWrapper from 'screens/Property/PropertiesLanding/PropertyLandingMap/PropertyMapWrapper';
import CropSeasonDisplay from 'components/CropSeasonDisplay';
import { ApplicationPropertyVO } from '../../hooks/useApplicationProperties';
import PropertiesTree from './components/PropertiesTree';
import DrawerFooter from './components/DrawerFooter';
import PropertySortingDropdown, {
  SortOption
} from './components/PropertySortingDropdown';
import {
  transformPropertiesToGeoJSON,
  calculateCentroidFromFeatures,
  calculateBoundingBoxCenter,
  calculateFitZoom,
  getAllExpandableKeys
} from './helpers';
import { filterPropertiesBySearch } from './components/PropertiesTree/helpers';
import useTreeSelection from './hooks/useTreeSelection';
import { DRAWER_DIMENSIONS, MAP_COLORS } from './constants';
import './ViewOnMapDrawer.css';

interface ViewOnMapDrawerProps {
  open: boolean;
  onClose: () => void;
  properties: ApplicationPropertyVO[];
  cropSeasonNames?: string;
}

/**
 * Check if any selected keys are no longer valid in the filtered properties
 */
const hasInvalidSelections = (
  selectedKeys: string[],
  filteredProperties: ApplicationPropertyVO[]
): boolean => {
  if (selectedKeys.length === 0) return false;

  const validIds = new Set<string>();
  // eslint-disable-next-line no-restricted-syntax
  for (const prop of filteredProperties) {
    validIds.add(prop.farmId);
    validIds.add(prop.fieldId);
    validIds.add(prop.cropZoneId);
    validIds.add(`crop-${prop.cropName || 'Unknown'}`);
  }

  return selectedKeys.some(key => {
    const baseId = key.split('-in-')[0];
    return !validIds.has(baseId) && !validIds.has(key);
  });
};

const ViewOnMapDrawer: React.FC<ViewOnMapDrawerProps> = ({
  open,
  onClose,
  properties,
  cropSeasonNames = ''
}) => {
  // Fit-all view, computed from ALL properties (not the search-filtered
  // subset) so it stays stable across search. Used as the map's initial
  // camera and the reset target on each open.
  const fitAllView = useMemo(() => {
    const { features } = transformPropertiesToGeoJSON(properties, []);
    return {
      zoom: [calculateFitZoom(features)],
      center: calculateBoundingBoxCenter(features)
    };
  }, [properties]);

  // Mount the map at the fit-all view — a fresh map drops a post-load fly-to
  // on first open, so the camera must be right at mount.
  const [zoom, setZoom] = useState(fitAllView.zoom);
  const [manualCoords, setManualCoords] = useState<[number, number] | null>(
    fitAllView.center
  );
  const [searchValue, setSearchValue] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.BY_FARM);

  // Filter properties for display (used in tree, footer stats, and map)
  const filteredProperties = useMemo(() => {
    return filterPropertiesBySearch(properties, searchValue);
  }, [properties, searchValue]);

  // Convert filtered properties to GeoJSON format - only show geometries matching search
  const geoJSONCollection = useMemo(() => {
    return transformPropertiesToGeoJSON(filteredProperties, selectedKeys);
  }, [filteredProperties, selectedKeys]);

  // Calculate centroid from all features
  const mapCoordinates = useMemo(() => {
    if (manualCoords) return manualCoords;
    return calculateCentroidFromFeatures(geoJSONCollection.features) || [0, 0];
  }, [geoJSONCollection.features, manualCoords]);

  const totalArea = useMemo(() => {
    return properties.reduce((sum, prop) => sum + prop.appliedArea, 0);
  }, [properties]);

  const totalCropZones = properties.length;
  const filteredCropZones = filteredProperties.length;
  const filteredTotalArea = useMemo(() => {
    return filteredProperties.reduce((sum, prop) => sum + prop.appliedArea, 0);
  }, [filteredProperties]);

  // Reset state when drawer opens
  useEffect(() => {
    let focusTimer: ReturnType<typeof setTimeout>;
    if (open) {
      setSearchValue(''); // Reset search value
      setSelectedKeys([]);
      setExpandedKeys([]);
      setSortOption(SortOption.BY_FARM); // Reset sort to BY_FARM
      // Camera reset is handled by the fit-all effect below.

      // Focus search input when drawer opens
      focusTimer = setTimeout(() => {
        const searchInput = document.querySelector<HTMLInputElement>(
          '[data-testid="search-input"]'
        );
        searchInput?.focus();
      }, 100);
    }
    // Clear on unmount/re-open: the timer otherwise fires after teardown and
    // dereferences `document` (crashes the test runner; harmless no-op in prod
    // but still a leak). clearTimeout(undefined) is a safe no-op when !open.
    return () => clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fit the map to all features once per open, as soon as fitAllView is
  // valid. Properties can load after the drawer opens, so mounting alone
  // would leave a stale camera until the first click. Gated by a ref so it
  // fires once per open and never clobbers the user's pan/zoom afterwards
  // (deselect stays a no-op). (CWFWEB-9716)
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!open) {
      didInitialFitRef.current = false;
      return;
    }
    if (didInitialFitRef.current) return;
    if (fitAllView.center) {
      setManualCoords(fitAllView.center);
      setZoom(fitAllView.zoom);
      didInitialFitRef.current = true;
    } else {
      // No bbox yet (no geometry, or properties still loading). Clear any
      // camera carried over from a previous open of this persistent drawer
      // so a stale view isn't shown; leave didInitialFitRef false so the
      // real fit-all still runs once geometry arrives.
      setManualCoords(null);
      setZoom(fitAllView.zoom);
    }
  }, [open, fitAllView]);

  // Reset selection and expansion when sort option changes
  // This gives a fresh view when switching between Group by Farm and Group by Crop
  useEffect(() => {
    setSelectedKeys([]);
    setExpandedKeys([]);
  }, [sortOption]);

  // Auto-expand nodes when searching to show matches at all levels
  useEffect(() => {
    if (searchValue && searchValue.trim() !== '') {
      // When searching, expand all nodes so matches are visible
      const allKeys = getAllExpandableKeys(
        filteredProperties,
        sortOption === SortOption.BY_CROP ? 'BY_CROP' : 'BY_FARM',
        searchValue
      );
      setExpandedKeys(allKeys);

      // Clear selection if selected items are filtered out
      if (hasInvalidSelections(selectedKeys, filteredProperties)) {
        setSelectedKeys([]);
      }
    } else {
      // When search is cleared, just collapse the tree
      setExpandedKeys([]);
    }
    // Note: selectedKeys is NOT in dependencies - we only check on search/filter changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, filteredProperties, sortOption]);

  // Handle drawer close
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Deselect = clear selection only. Leave the map's pan/zoom alone so the
  // user stays where they were looking. Per UX: "if I deselect the field
  // or the farm, the map should not adjust." The fit-to-all view only
  // applies on initial open.
  const handleDeselect = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  // Use custom hook for tree selection and map navigation
  const {
    handleFieldClick,
    handleFarmClick,
    handleCropClick
  } = useTreeSelection({
    selectedKeys,
    setSelectedKeys,
    setExpandedKeys,
    setManualCoords,
    setZoom,
    geoJSONFeatures: geoJSONCollection.features,
    onDeselect: handleDeselect
  });

  return (
    <Drawer
      width={DRAWER_DIMENSIONS.WIDTH}
      open={open}
      onClose={handleClose}
      closable={false}
      bodyStyle={{
        overflow: 'hidden',
        borderBottom: 'none',
        padding: '24px 24px 0 24px'
      }}
      className="view-on-map-drawer"
    >
      <div data-testid="view-on-map-drawer" className="h-full flex flex-col">
        <header className="flex flex-row mb-3 shrink-0 items-center">
          <h1 className="text-2xl font-semibold flex-1 m-0 leading-tight">
            View Selected Properties
          </h1>

          <button
            type="button"
            onClick={handleClose}
            className="ml-4 w-8 h-8 hover:bg-gray-100 rounded cursor-pointer flex items-center justify-center"
            aria-label="Close drawer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"
                fill="#696F88"
              />
            </svg>
          </button>
        </header>

        <div className="flex flex-row flex-1 overflow-hidden">
          <section
            className="flex flex-col w-2/5 mr-6 tree-left-panel"
            style={{ minWidth: 0, flexShrink: 0 }}
          >
            <div className="text-xs font-normal" style={{ color: '#696F88' }}>
              Crop Season
            </div>
            <CropSeasonDisplay cropSeasonNames={cropSeasonNames} />
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1">
                <SearchInputLandingProperties
                  drawer
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const valueInput = event.target.value.toLowerCase();
                    setSearchValue(valueInput);
                  }}
                  value={searchValue}
                  placeholder="Search Properties"
                />
              </div>
              <PropertySortingDropdown
                onSortChange={setSortOption}
                defaultSort={sortOption}
              />
            </div>
            <div
              className="flex-1"
              style={{
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarGutter: 'stable'
              }}
            >
              <PropertiesTree
                properties={properties}
                treeState={{
                  selectedKeys,
                  expandedKeys,
                  onExpand: setExpandedKeys
                }}
                handlers={{
                  onFarmClick: handleFarmClick,
                  onFieldClick: handleFieldClick,
                  onCropClick: handleCropClick
                }}
                sortBy={sortOption}
                searchTerm={searchValue}
              />
            </div>
          </section>

          <section
            className="w-3/5 view-on-map-section"
            style={{ minWidth: 0, flexShrink: 0 }}
          >
            {/*
              Not remounted on open (no key={mapKey}): a fresh map dropped the
              first selection's camera command while initializing. The open
              effect already resets the view to fit-all.
            */}
            <PropertyMapWrapper
              // Drive pan/zoom from the tree selection (useTreeSelection);
              // opt out of PropertyFeatureLayer's auto-fit so it doesn't fight
              // the drawer's camera. See CWFWEB-9716.
              disableAutoFit
              geoJSONCollection={geoJSONCollection}
              zoom={zoom}
              setZoom={setZoom}
              currentCoordinates={{ coordinates: mapCoordinates }}
              setCurrentCoordinates={setManualCoords}
              fieldsLoading={false}
              displayOnly
              stretch
              labelName=""
              showArchivedLabels
              customStyles={
                selectedKeys.length > 0
                  ? {
                      // Custom layer key — must match the $layer property set
                      // by transformPropertiesToGeoJSON. NOT 'focused', so
                      // PropertyFeatureLayer's bbox-fit effect (which auto-
                      // fitBounds on FOCUSED_LAYER_TYPE changes) ignores
                      // selection toggles in this drawer.
                      'aw-applied-area-selected': {
                        fillColor: MAP_COLORS.FOCUSED_FILL,
                        fillOutlineColor: MAP_COLORS.FOCUSED_OUTLINE
                      }
                    }
                  : undefined
              }
            />
          </section>
        </div>

        <DrawerFooter
          totalCropZones={searchValue ? filteredCropZones : totalCropZones}
          totalArea={searchValue ? filteredTotalArea : totalArea}
          onClose={handleClose}
        />
      </div>
    </Drawer>
  );
};

export default ViewOnMapDrawer;
